import React, { useEffect, useState, useContext } from "react";

import { useHistory, useLocation } from "react-router";
import { isEmpty } from "lodash";
import { Trans, useTranslation } from "react-i18next";
import Tippy from "@tippyjs/react";
import LoadingOverlay from "../../components/Loaders/LoadingOverlay";

import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import VersionTable from "../../components/Tables/VersionTable";
import Pagination from "../../components/Pagination/Pagination";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import ModelVersionsAPI from "../../API/modelVersions.api";
import LookupsAPI from "../../API/lookups.api";
import ModelsAPI from "../../API/models.api";
import ModelParametersAPI from "../../API/modelParameters.api";
import { longDate } from "../../utils/DateHelper";
import UserContext from "../../lib/UserContext";
import { ROLES, MODEL_ACCESS } from "../../utils/constants";
import { getMappedFilters } from "../../utils/FilterHelpers";
import BreadCrumb from "../../components/Navbars/BreadCrumb";
import LazyImage from "../../components/Shared/LazyImage";
import LogCapture from "../../utils/LogManagement/LogHelper";
import ParameterSettingsModal from "../../components/Modals/ParameterSettingsModal";
import { isAuthorized } from "../../utils/RolesHelpers";
import { getRoles } from "../../utils/OktaHelpers";
import BaseAlertModal from "../../components/Modals/BaseAlertModal";
import EditableImage from "../../components/Images/EditableImages";
import { Link } from "react-router-dom";
import { setObject, getObject } from "../../utils/StorageHelpers";
import MissingData from "../../components/Shared/MissingData";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";
import { getMappedParameterLocalizations } from "../../utils/generalMappers";
import useTranslationCallback from "../../utils/CustomHooks/useTranslationCallback";

const ModelEdit = () => {
    const [model, setModel] = useState({});
    const [user, setUser] = useState({});
    const [currentRoles, setCurrentRoles] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [versions, setVersions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [filtersUsed, setFiltersUsed] = useState(false);
    const [deleteButton, setDeleteButton] = useState(false);
    const [isEditModelMode, setIsEditModelMode] = useState(false);
    const [editModelInput, setEditModelInput] = useState({});
    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [activeVersion, setActiveVersion] = useState({});

    const [accessType, setAccessType] = useState("");

    const [forceDeployModal, setForceDeployModal] = useState(false);

    const [isParameterSettingsValid, setIsParameterSettingsValid] =
        useState(false);

    const [isParameterSettingsSubmitted, setIsParameterSettingsSubmitted] =
        useState(false);
    const [parameterSettings, setParameterSettings] = useState([]);
    const [existingUnits, setExistingUnits] = useState([]);
    const [activeUnit, setActiveUnit] = useState([]);
    const [parameterSettingsModal, setParameterSettingsModal] = useState(false);
    const [isParameterSettingsSubmitting, setIsParameterSettingsSubmitting] =
        useState(false);

    const { t } = useTranslation();

    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Versions-itemsPerPage", true) || 10
    );

    const history = useHistory();
    const context = useContext(UserContext);
    const location = useLocation();
    const modelParameterId = location.state?.modelParameterId || null;

    const INDEX = 0;

    const headers = [
        {
            label: t("active"),
            skipSort: true,
        },
        { label: t("version"), skipSort: true },
        { label: t("description"), skipSort: true },
        { label: t("publishedAt"), skipSort: true },
        { label: t("modelType"), skipSort: true },
        { label: t("range"), skipSort: true },
        { label: t("threshold"), skipSort: true },
        {
            label: "R2 Test",
            element: (
                <span className="ml-2">
                    R<sup>2</sup> Test
                </span>
            ),
            skipSort: true,
        },
        { label: t("rmseTest"), skipSort: true },
        {
            label: t("r2Cv"),
            element: (
                <span className="ml-2">
                    R<sup>2</sup> CV
                </span>
            ),
            skipSort: true,
        },
        { label: t("rmseCv"), skipSort: true },
        { label: t("BiasTest"), skipSort: true },
        { label: t("RPDTest"), skipSort: true },
        { label: t("BiasCV"), skipSort: true },
        { label: t("RPDCV"), skipSort: true },
        { label: t("numberOfSamples"), skipSort: true },
        { label: t("supportedAccessories"), skipSort: true },
        { label: t("actions"), skipSort: true, classNames: "sticky-header" },
    ];

    const searchFilters = [
        {
            label: t("description"),
            name: "description",
            type: "Input",
            placeholder: t("descriptionPlaceholder"),
        },
        {
            label: t("version"),
            name: "version",
            type: "Input",
            placeholder: t("versionPlaceholder"),
        },
        {
            label: t("numberOfMeasurements"),
            name: "numberOfMeasurements",
            type: "Input",
            inputType: "number",
            placeholder: t("numberOfMeasurementsPlaceholder"),
        },
        {
            label: t("active"),
            name: "active",
            type: "Dropdown",
            options: [
                { value: true, label: t("active") },
                { value: false, label: t("inactive") },
            ],
        },
    ];

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/models",
            element: () => <Trans i18nKey="models">Models</Trans>,
        },
        {
            element: () => <Trans i18nKey="viewModel">View Model</Trans>,
        },
    ];

    useTranslationCallback(() => {
        if (modelParameterId) {
            getParameterDetails();
        }
    }, []);

    useEffect(() => {
        if (modelParameterId) {
            setIsLoading(true);
            setCurrentRoles(getRoles());
            setUser(context.getUser());
            fetchVersions(modelParameterId);
        } else {
            history.push("/models");
        }
    }, []);

    useEffect(() => {
        if (model) {
            fetchUserAccess().then((modelAccess) => {
                if (
                    user.role === ROLES.support ||
                    user.role === ROLES.partnerAdmin
                ) {
                    setAccessType(MODEL_ACCESS.owner);
                } else {
                    const userAccess = modelAccess?.modelUserAccesses?.filter(
                        (access) => {
                            return access.userId === user.id;
                        }
                    );
                    if (!isEmpty(userAccess)) {
                        userAccess[0]?.write
                            ? setAccessType(MODEL_ACCESS.write)
                            : setAccessType(MODEL_ACCESS.read);
                    }
                }
            });
        }
    }, [model]);

    const fetchVersions = async (modelId, _filters = null, offset = 0) => {
        setIsLoading(true);
        const newFilter = getMappedFilters(_filters);
        const _offset = offset < 0 ? 0 : offset;

        if (modelId) {
            let result = await ModelVersionsAPI.getModelVersions(
                modelId,
                itemsPerRequest,
                newFilter,
                _offset
            );
            setVersions(result?.data);
            setTotalCount(result?.totalCount || 0);
            setActiveVersion(getActiveVersion(result?.data));
        }
        setIsLoading(false);
    };

    const fetchUserAccess = async () => {
        if (model.modelId) {
            return LookupsAPI.getAccessByModelId(model.modelId);
        }
    };

    const getActiveVersion = (versions) => {
        return versions.find((version) => version.active);
    };

    const applyFilters = (specificApplyFilters) => {
        fetchVersions(modelParameterId, specificApplyFilters);
        setFiltersUsed(true);
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            // call API to get model versions with the next pagination
            fetchVersions(modelParameterId, filters, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Versions-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        fetchVersions(modelParameterId, filters, 0, itemsPerPage);
    };

    const handleDeployClick = async (force = false) => {
        try {
            let result = await ModelParametersAPI.deployModel({
                id: modelParameterId,
                force,
            }); // force based on modal
            if (result) {
                setModel({ ...model, deployed: !model.deployed });
                LogCapture.info(
                    `Model deployed with id: ${modelParameterId}`,
                    ENTITIES.modelParameters,
                    model.deployed
                        ? ACTIONS.modelParameters.deployModel
                        : ACTIONS.modelParameters.undeployModel
                );
            } else {
                // display error message & display modal to force deployment
                setForceDeployModal(true);
                LogCapture.error(
                    `Couldn't deploy model with id: ${modelParameterId}`
                );
            }
        } catch (error) {
            LogCapture.error(
                `Couldn't deploy model with id: ${modelParameterId}, error: ${
                    error?.message || error
                }`
            );
            ErrorAlert(error?.message || error);
        }
    };

    const handleActivateVersion = async (index) => {
        setIsLoading(true);
        let versionId = versions[index].id;
        let result = await ModelVersionsAPI.activateVersion({
            modelParameterId,
            versionId,
        });
        if (result) {
            let tempVersions = [...versions];
            tempVersions.forEach((version) => {
                version.active = false;
            });
            tempVersions[index].active = true;
            setVersions(tempVersions);
            LogCapture.info(`Version activated with id: ${modelParameterId}`);
            SuccessAlert("Version activated successfully!");
        } else {
            ErrorAlert(t("couldNotActivateVersionMessage"));
            LogCapture.error(
                `Couldn't activate version with id: ${modelParameterId}`
            );
        }
        setIsLoading(false);
    };

    const handleDeleteVersion = async (index) => {
        let versionId = versions[index]?.id;
        try {
            let result = await ModelVersionsAPI.deleteVersion(versionId);
            if (!isEmpty(result)) {
                let newCurrentPage;

                if (versions.length > 1) {
                    newCurrentPage = currentPage;
                } else if (currentPage > 1) {
                    newCurrentPage = currentPage - 1;
                } else {
                    newCurrentPage = currentPage;
                }
                LogCapture.info(
                    `Version deleted with id: ${versionId}`,
                    ENTITIES.modelParameters,
                    ACTIONS.modelParameters.versionDelete
                );

                setCurrentPage(newCurrentPage);
                fetchVersions(modelParameterId, filters, newCurrentPage - 1);
                SuccessAlert(t("deleteModelVersionSuccessMessage"));
                return true;
            } else {
                LogCapture.error(
                    `Couldn't delete version with id: ${versionId}`
                );
                return false;
            }
        } catch (error) {
            ErrorAlert(t("deleteModelVersionFailMessage"));
            LogCapture.error(
                `Couldn't delete version with id: ${versionId}, error: ${error}`
            );
        }
    };

    const handleDeleteModel = async () => {
        setIsLoading(true);
        return ModelParametersAPI.deleteModel(modelParameterId)
            .then((result) => {
                if (result) {
                    history.push("/models");
                    SuccessAlert(t("deleteModelSuccessMessage"));
                    LogCapture.info(
                        `Model deleted with id: ${modelParameterId}`,
                        ENTITIES.modelParameters,
                        ACTIONS.modelParameters.delete
                    );
                } else {
                    ErrorAlert(t("deleteModelFailMessage"));
                    LogCapture.error(
                        `Couldn't delete model with id: ${modelParameterId}`
                    );
                }
            })
            .catch((error) => {
                console.log(error);
                ErrorAlert(t("deleteModelFailMessage"));
                LogCapture.error(
                    `Couldn't delete model with id: ${modelParameterId}, error: ${error}`
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const enterEditModelMode = () => {
        setIsEditModelMode(true);
    };

    const handleUpdateModel = () => {
        setIsSubmitting(true);
        const modelData = {
            id: model.modelId,
            ...editModelInput,
        };
        ModelsAPI.updateModel(modelData)
            .then((res) => {
                if (!isEmpty(res)) {
                    setModel({
                        ...model,
                        ...editModelInput,
                        modelDescription: editModelInput.description,
                    });
                    SuccessAlert(t("editModelSuccessMessage"));
                    LogCapture.info(
                        `Model updated with id: ${model?.modelId}`,
                        ENTITIES.models,
                        ACTIONS.models.updateModel
                    );
                } else {
                    LogCapture.error(
                        `Couldn't update model with id: ${model?.modelId}`
                    );
                }

                setIsEditModelMode(false);
            })
            .catch((error) => {
                console.log(error);
                LogCapture.error(
                    `Couldn't update model with id: ${model?.modelId}, error: ${error}`
                );
                ErrorAlert(t("editModelFailMessage"));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const getParameterSettingsValue = (key, subKey) => {
        return (
            !isEmpty(parameterSettings) &&
            (subKey
                ? parameterSettings[INDEX][key][INDEX][subKey]
                : parameterSettings[INDEX][key])
        );
    };

    const mapParameterSettingsPayload = () => {
        const newParameterDetails = [
            ...getParameterSettingsValue("parameterDetails"),
        ];
        newParameterDetails.splice(0, 1);
        const units = newParameterDetails.map((unit, index) => {
            return {
                id: unit.unit.value,
                decimalPlaces: unit.decimalPlaces,
                displayUnit: activeUnit[0] === index + 1,
            };
        });

        const deletedUnits = getDeletedUnits(units);

        return [...units, ...deletedUnits];
    };

    const getDeletedUnits = (units) => {
        const deletedUnits = [];
        const newExisting = [...existingUnits];
        newExisting.splice(0, 1);
        newExisting.forEach((existingUnit) => {
            let currentDeleted = true;
            units.forEach((unit) => {
                if (existingUnit.unit.value === unit.id) currentDeleted = false;
            });
            currentDeleted &&
                deletedUnits.push({
                    id: existingUnit.unit.value,
                    isDeleted: true,
                });
        });
        return deletedUnits;
    };

    const handleUpdateParameterSettings = async () => {
        setIsParameterSettingsSubmitted(true);

        if (!isParameterSettingsValid) return;

        setIsParameterSettingsSubmitting(true);

        const payload = {
            modelParameterId,
            parameterName: getParameterSettingsValue("name"),
            decimalPlaces:
                +getParameterSettingsValue(
                    "parameterDetails",
                    "decimalPlaces"
                ) ?? 2,
            units: mapParameterSettingsPayload(),
            modelParameterTranslations: getMappedParameterLocalizations(
                getParameterSettingsValue("localizations")
            ),
        };

        const result = await ModelParametersAPI.updateParameterSettings(
            payload
        );
        if (result?.modified) {
            SuccessAlert(t("editModelSuccessMessage"));
            LogCapture.info(
                `Model parameter settings updated with id: ${model?.modelId}`,
                ENTITIES.modelParameters,
                ACTIONS.modelParameters.parameterSettingsUpdate
            );

            const modifiedParameter = parameterSettings[0] || {};
            setModel({
                ...model,
                parameterSettings: {
                    parameterName:
                        modifiedParameter?.name ||
                        model.parameterSettings?.parameterName,
                    unit: modifiedParameter?.parameterDetails[0]?.unit?.label,
                    decimalPlaces:
                        modifiedParameter?.parameterDetails[0]?.decimalPlaces,
                },
            });
            setParameterSettingsModal(false);
        } else {
            LogCapture.error(
                `Couldn't update model parameter settings with id: ${model?.modelId}`
            );
            ErrorAlert(t("editModelFailMessage"));
        }
        setIsParameterSettingsSubmitting(false);
    };

    const mapLocalizations = (localizations) => {
        const mappedLocalizations = {};

        localizations.forEach((localization) => {
            mappedLocalizations[localization.languageId] = localization.value;
        });

        return mappedLocalizations;
    };

    const mapParameter = (parameterSettings) => {
        const activeUnits = [];
        const baseUnit = {
            unit: {
                label: parameterSettings.unit,
                value: parameterSettings.unitId,
            },
            decimalPlaces: parameterSettings.decimalPlaces,
        };
        const conversionUnits = parameterSettings.units.map((unit, index) => {
            unit.displayUnit && activeUnits.push(index + 1);
            return {
                unit: {
                    label: unit.unitConversion,
                    value: unit.unitConversionId,
                    modelParameterId: unit.modelParameterId,
                },
                decimalPlaces: unit.decimalPlaces,
            };
        });
        setActiveUnit(isEmpty(conversionUnits) ? [1] : activeUnits);

        return [
            {
                name: parameterSettings.parameterName,
                displayName: parameterSettings.parameterName,
                localizations: {
                    en: "",
                    ...mapLocalizations(
                        parameterSettings.modelParameterTranslations
                    ),
                },
                parameterDetails: [baseUnit, ...conversionUnits],
            },
        ];
    };

    const getParameterDetails = async () => {
        setIsSubmitting(true);
        const result = await ModelParametersAPI.getModelParameterDetails(
            modelParameterId
        );
        setExistingUnits(
            mapParameter(result.parameterSettings)[0].parameterDetails
        );
        setParameterSettings(mapParameter(result.parameterSettings));
        setModel(result);
        setEditModelInput({
            modelName: result.modelName,
            image: result.modelImage,
            description: result.modelDescription,
        });
        setIsSubmitting(false);
    };

    const handleInputChange = (e) => {
        setEditModelInput({
            ...editModelInput,
            [e.target.name]: e.target.value,
        });
    };

    const hasAccess = (type) => {
        return type === accessType ? true : false;
    };

    const hasEditAccess = () => {
        return hasAccess(MODEL_ACCESS.owner);
        // || hasAccess(MODEL_ACCESS.write);
    };

    const hasNewVersionAccess = () => {
        const authRoles = [ROLES.superAdmin, ROLES.support];
        const currentRoles = getRoles();

        return (
            isAuthorized(authRoles, currentRoles) ||
            (isAuthorized([ROLES.partnerAdmin], currentRoles) &&
                user?.organization?.uploadModelAccess)
        );
    };

    const editVersionCompeletion = async (modifiedVersion) => {
        const newVersions = [...versions];
        let selectedVersion = newVersions.find(
            (version) => version.id === modifiedVersion.id
        );
        selectedVersion.version =
            modifiedVersion?.version || selectedVersion?.version || 0;
        selectedVersion.description =
            modifiedVersion?.description || selectedVersion?.description || 0;
        selectedVersion.modelInfo.minValue =
            modifiedVersion?.minValue ||
            selectedVersion?.modelInfo?.minValue ||
            0;
        selectedVersion.modelInfo.maxValue =
            modifiedVersion?.maxValue ||
            selectedVersion?.modelInfo?.maxValue ||
            0;

        selectedVersion.modelInfo.isVisible =
            modifiedVersion?.isVisible || false;

        if (modifiedVersion.supportedAccessories) {
            selectedVersion.supportedAccessories =
                modifiedVersion.supportedAccessories
                    .filter((acc) => !acc.isDeleted)
                    .map((acc) => ({ label: acc.name, value: acc.id }));
        }

        setVersions(newVersions);
    };

    const isEditModelNameEmpty = (trueReturn, falseReturn) => {
        return isEmpty(editModelInput.modelName) ? trueReturn : falseReturn;
    };

    const isAdminAuthorized = () => {
        return isAuthorized([ROLES.partnerAdmin, ROLES.support], currentRoles);
    };

    const isAllowedToEditVersion = () => {
        return (
            isAuthorized([ROLES.superAdmin, ROLES.support], getRoles()) ||
            (isAuthorized([ROLES.partnerAdmin], getRoles()) &&
                user?.organization?.uploadModelAccess)
        );
    };

    const renderEditModelSection = () => {
        if (isSubmitting) {
            return (
                <span className="animate-spin mr-3 inline-flex">
                    <i className="fas fa-sync" />
                </span>
            );
        } else
            return (
                <div className="header-card-actions flex items-center">
                    {hasEditAccess() &&
                        (!isEditModelMode ? (
                            <div className="actions-box cursor-pointer">
                                <i
                                    className="fas fa-edit"
                                    onClick={enterEditModelMode}
                                />
                            </div>
                        ) : (
                            <>
                                <div className="actions-box cursor-pointer">
                                    <i
                                        className="fas fa-times"
                                        onClick={() =>
                                            setIsEditModelMode(false)
                                        }
                                    />
                                </div>
                                <div className="actions-box">
                                    <div
                                        className={` ${isEditModelNameEmpty(
                                            "cursor-not-allowed",
                                            ""
                                        )}`}
                                    >
                                        <i
                                            className={`fas fa-save ${isEditModelNameEmpty(
                                                "disabled",
                                                ""
                                            )}`}
                                            disabled={`${isEditModelNameEmpty(
                                                "disabled",
                                                ""
                                            )}`}
                                            onClick={handleUpdateModel}
                                        />
                                    </div>
                                </div>
                            </>
                        ))}

                    <div className="actions-box cursor-pointer text-primary-orange">
                        {hasAccess(MODEL_ACCESS.owner) && (
                            <i
                                className="text-primary-orange fas fa-trash-alt"
                                onClick={() => setDeleteButton(true)}
                            />
                        )}
                    </div>
                </div>
            );
    };

    return (
        <>
            <div className="product-section">
                <BreadCrumb crumbs={breadCrumbs} />

                <LoadingOverlay active={isLoading} text="Loading...">
                    <div className="model-editor">
                        <div className="product-header card-table shadow mb-5 flex items-center">
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center w-full">
                                    {isEditModelMode ? (
                                        <EditableImage
                                            state={editModelInput}
                                            stateSetter={setEditModelInput}
                                        />
                                    ) : (
                                        <LazyImage
                                            src={
                                                editModelInput.image ||
                                                require("../../assets/images/neural.png")
                                                    .default
                                            }
                                            className="card-header-img"
                                            alt="UserImage"
                                        />
                                    )}

                                    <span className="mx-3 w-full">
                                        {isEditModelMode ? (
                                            <>
                                                <input
                                                    type="text"
                                                    name="modelName"
                                                    value={
                                                        editModelInput.modelName
                                                    }
                                                    onChange={handleInputChange}
                                                    placeholder="ex. model name"
                                                    className="input-element"
                                                />
                                            </>
                                        ) : (
                                            <h3 className="medium-title-bold">
                                                {model.modelName}
                                            </h3>
                                        )}
                                    </span>
                                </div>
                                {renderEditModelSection()}
                            </div>
                        </div>
                        <div id="model-content" className="model-content">
                            <div className="description-title product-info-description mb-6">
                                <h3>
                                    <Trans i18nKey="description">
                                        Description
                                    </Trans>
                                </h3>
                                {isEditModelMode ? (
                                    <>
                                        <textarea
                                            name="description"
                                            value={
                                                editModelInput.description || ""
                                            }
                                            onChange={handleInputChange}
                                            placeholder="Type your description here..."
                                            className="input-element min-h-80"
                                        ></textarea>
                                    </>
                                ) : (
                                    <p>
                                        {model.modelDescription ||
                                            "No Description Found"}
                                    </p>
                                )}
                            </div>
                            <div className="prod-title product-info-specification mb-6">
                                {/* <h3>Specifications</h3> */}
                                <ul className="specification-list flex justify-between">
                                    <li className="input-group">
                                        <label className="uppercase">
                                            <Trans i18nKey="materialName">
                                                Material Name
                                            </Trans>
                                        </label>
                                        <div className="specification-list-title blue-color">
                                            {model.materialName}
                                        </div>
                                    </li>
                                    <li className="input-group">
                                        <div className="flex justify-between">
                                            <label className="uppercase">
                                                <Trans i18nKey="parameterName">
                                                    Parameter Name
                                                </Trans>
                                            </label>
                                            {isAdminAuthorized() && (
                                                <Tippy
                                                    content={t(
                                                        "setParameterSettings"
                                                    )}
                                                    placement="top"
                                                    animation="scale"
                                                >
                                                    <i
                                                        onClick={() =>
                                                            setParameterSettingsModal(
                                                                true
                                                            )
                                                        }
                                                        className="fas fa-cog fa-sm mr-2 text-primary-color-orange cursor-pointer"
                                                    ></i>
                                                </Tippy>
                                            )}
                                        </div>
                                        <div className="specification-list-title blue-color">
                                            {
                                                model.parameterSettings
                                                    ?.parameterName
                                            }
                                        </div>
                                    </li>
                                    <li className="input-group">
                                        <label className="uppercase">
                                            <Trans i18nKey="createdBy">
                                                Created By
                                            </Trans>
                                        </label>
                                        <div className="specification-list-title blue-color">
                                            {model.createdBy || "---"}
                                        </div>
                                    </li>
                                    <li className="input-group">
                                        <label className="uppercase">
                                            <Trans i18nKey="createdAt">
                                                Created At
                                            </Trans>
                                        </label>
                                        <div className="specification-list-title blue-color">
                                            {longDate(model.createdAt)}
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <header className="main-content-header flex items-center justify-between">
                                <h2>
                                    <Trans i18nKey="versions">Versions</Trans>
                                </h2>
                                <div className="action-header-wrapper flex items-center">
                                    <FilterDropdown
                                        filterOptions={searchFilters}
                                        filters={filters}
                                        setFilters={setFilters}
                                        applyFilters={applyFilters}
                                        appliedFilters={appliedFilters}
                                        setAppliedFilters={setAppliedFilters}
                                        component="versions"
                                    />
                                    {(hasAccess(MODEL_ACCESS.owner) ||
                                        hasAccess(MODEL_ACCESS.write)) && (
                                        <button
                                            className="default-orange-outline-btn ml-2"
                                            onClick={() => handleDeployClick()}
                                        >
                                            {model.deployed
                                                ? "Undeploy"
                                                : "Deploy"}
                                        </button>
                                    )}
                                    {hasNewVersionAccess() && (
                                        <Link
                                            to={{
                                                pathname: "/models/upload",
                                                state: {
                                                    data: {
                                                        ...model,
                                                        id: modelParameterId,
                                                    },
                                                    modelType:
                                                        activeVersion?.modelType,
                                                },
                                            }}
                                        >
                                            <button className="default-orange-outline-btn ml-2">
                                                <i className="fas fa-plus mr-2" />
                                                <Trans i18nKey="newVersion">
                                                    New Version
                                                </Trans>
                                            </button>
                                        </Link>
                                    )}
                                </div>
                            </header>
                        </div>

                        <div className="card-table shadow">
                            {versions.length > 0 ? (
                                <>
                                    <VersionTable
                                        headers={headers}
                                        rows={versions}
                                        selectedVersion={selectedVersion}
                                        setSelectedVersion={setSelectedVersion}
                                        handleActivate={handleActivateVersion}
                                        handleDelete={handleDeleteVersion}
                                        accessType={accessType}
                                        editVersionCompeletion={
                                            isAllowedToEditVersion() &&
                                            editVersionCompeletion
                                        }
                                        modelParameterId={modelParameterId}
                                        modelId={model.modelId}
                                        model={model}
                                    />
                                    <Pagination
                                        currentPage={currentPage}
                                        setCurrentPage={handlePageClick}
                                        totalCount={totalCount}
                                        sizePerPage={itemsPerRequest}
                                        handleItemsPerPage={handleItemsPerPage}
                                    />
                                </>
                            ) : (
                                <MissingData
                                    isFilterUsed={filtersUsed}
                                    entity={t("versions")}
                                    includeText={!isLoading}
                                    path="/users/form"
                                />
                            )}
                        </div>
                    </div>
                </LoadingOverlay>
            </div>
            <BaseAlertModal
                isVisible={deleteButton}
                setIsVisible={setDeleteButton}
                title={t("deleteParameterTitle", {
                    parameterName: model.parameterSettings?.parameterName,
                })}
                message={t("deleteParameterMessage")}
                action={{ submitAction: handleDeleteModel }}
                type="delete"
            />

            <BaseAlertModal
                isVisible={forceDeployModal}
                setIsVisible={setForceDeployModal}
                title={t("forceDeployModalTitle")}
                message={t("forceDeployModalMessage")}
                action={{
                    submitAction: () => handleDeployClick(true),
                }}
                type="warning"
            />
            {isAdminAuthorized() && (
                <ParameterSettingsModal
                    isSubmitting={isParameterSettingsSubmitting}
                    isVisible={parameterSettingsModal}
                    setIsVisible={setParameterSettingsModal}
                    parameterSettings={parameterSettings}
                    setParameterSettings={setParameterSettings}
                    activeUnit={activeUnit}
                    setActiveUnit={setActiveUnit}
                    action={handleUpdateParameterSettings}
                    isFormValid={isParameterSettingsValid}
                    setIsFormValid={setIsParameterSettingsValid}
                    isFormSubmitted={isParameterSettingsSubmitted}
                    getParameterSettingsValue={getParameterSettingsValue}
                    isDynamicSorted={model.isDynamicSorted}
                />
            )}
        </>
    );
};

export default ModelEdit;
